import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UserX, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

export default function OrphanAccounts({ refreshKey }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/roster/orphan-accounts"); setList(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const revoke = async (u) => {
    if (!(await confirm({
      title: `Delete ${u.name || u.email}'s login?`,
      description: `${u.email} is not on the roster. Deleting the account removes their access to the platform. This cannot be undone.`,
      confirmText: "Delete login", destructive: true,
    }))) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Login removed for ${u.email}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  if (!list.length) return null;
  return (
    <div className="p-3 rounded-lg border border-rose-200 bg-rose-50/60 text-xs text-rose-900" data-testid="roster-orphan-accounts">
      <button type="button" onClick={() => setOpen((o) => !o)} className="font-semibold inline-flex items-center gap-2" data-testid="roster-orphan-toggle">
        <UserX className="w-4 h-4" /> {list.length} student login{list.length === 1 ? "" : "s"} not on the roster — still able to sign in
        <span className="font-normal text-rose-700">{open ? "hide" : "show"}</span>
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-rose-200">
          {list.map((u) => (
            <li key={u.id} className="flex items-center justify-between py-1.5" data-testid={`orphan-account-${u.id}`}>
              <span><span className="font-semibold">{u.name || "—"}</span> <span className="font-mono text-rose-700">{u.email}</span></span>
              <Button variant="ghost" size="sm" onClick={() => revoke(u)} className="text-rose-700 hover:bg-rose-100 h-7" data-testid={`orphan-delete-${u.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete login
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
